import { Profile } from '@supsupclub/types';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { FETCH_PROFILE_QUERY, updateProfile } from '../endpoints';
import { useSettingsStore } from '../stores';
import { useAuth } from './use-auth';

type SupportedLocale = 'en' | 'de' | 'pt' | 'nl';

const SUPPORTED_LOCALES: readonly string[] = ['en', 'de', 'pt', 'nl'];

const isSupportedLocale = (lang: string): lang is SupportedLocale =>
  SUPPORTED_LOCALES.includes(lang);

const normalizeLanguage = (lang: string) => lang.toLowerCase();

export const useLanguageSync = () => {
  const { profile, isLoggedIn } = useAuth();
  const locale = useSettingsStore((store) => store.locale);
  const setLocale = useSettingsStore((store) => store.setLocale);
  const queryClient = useQueryClient();

  const isSyncingFromProfile = useRef(false);
  const localeRef = useRef(locale);

  localeRef.current = locale;

  const profileLanguage = profile?.language
    ? normalizeLanguage(profile.language)
    : undefined;

  useEffect(() => {
    if (
      isLoggedIn &&
      profileLanguage &&
      isSupportedLocale(profileLanguage) &&
      profileLanguage !== localeRef.current
    ) {
      isSyncingFromProfile.current = true;
      setLocale(profileLanguage);
    }
  }, [isLoggedIn, profileLanguage, setLocale]);

  useEffect(() => {
    if (isSyncingFromProfile.current) {
      isSyncingFromProfile.current = false;
      return;
    }

    if (isLoggedIn && profile?.id && locale !== profileLanguage) {
      updateProfile(profile.id, { language: locale });
      queryClient.setQueryData<Profile>([FETCH_PROFILE_QUERY], (old) =>
        old ? { ...old, language: locale } : old,
      );
    }
  }, [locale, isLoggedIn, profile?.id, profileLanguage, queryClient]);
};
